import { useMemo } from 'react';

const isFilled = (value) => value !== null && value !== undefined && String(value).trim() !== '';

/**
 * Profile completion, weighted from three parts:
 *   - personal details (40%): how many of the profile fields are filled
 *   - profile photo (20%)
 *   - training (40%): assigned SOPs read + assigned trainings watched
 * With nothing assigned the training part counts as done, so such a user can
 * still reach 100%. `missing` lists the personal fields still empty.
 */
const useProfileCompletion = (userData, profileImageUrl, { mainSOPList, readSOPs, indicationEducations, viewedIndicationEducations }) => useMemo(() => {
  const personalFields = {
    eName: userData.eName,
    eSurname: userData.eSurname,
    role: userData.role,
    eMail: userData.eMail,
    phone: userData.phone,
    emergencyPhone: userData.emergencyPhone,
    address: userData.address,
  };
  const keys = Object.keys(personalFields);
  const filledCount = keys.filter((key) => isFilled(personalFields[key])).length;
  const personalPercent = Math.round((filledCount / keys.length) * 100);

  const photoPercent = profileImageUrl ? 100 : 0;

  const totalAssigned = mainSOPList.length + indicationEducations.length;
  const completedSops = mainSOPList.filter((sop) => readSOPs.has(sop.id)).length;
  const completedEducations = indicationEducations.filter((edu) => viewedIndicationEducations.has(edu.id)).length;
  const completedAssigned = completedSops + completedEducations;
  const educationPercent = totalAssigned === 0 ? 100 : Math.round((completedAssigned / totalAssigned) * 100);

  return {
    overall: Math.round(personalPercent * 0.4 + photoPercent * 0.2 + educationPercent * 0.4),
    personal: { percent: personalPercent, filled: filledCount, total: keys.length, missing: keys.filter((key) => !isFilled(personalFields[key])) },
    photo: { percent: photoPercent },
    education: { percent: educationPercent, completed: completedAssigned, total: totalAssigned, readSops: completedSops, sopTotal: mainSOPList.length },
  };
}, [
  userData.eName,
  userData.eSurname,
  userData.role,
  userData.eMail,
  userData.phone,
  userData.emergencyPhone,
  userData.address,
  profileImageUrl,
  mainSOPList,
  readSOPs,
  indicationEducations,
  viewedIndicationEducations,
]);

export default useProfileCompletion;
